import React, { useState, useEffect } from 'react';
import { getTransactionHistory, Transaction } from '../services/creditsService';

const PendingEarningsPanel: React.FC = () => {
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [totalPending, setTotalPending] = useState<number>(0);

  useEffect(() => {
    // Get pending sales
    const transactions = getTransactionHistory();
    const pending = transactions.filter(t => t.type === 'Sale' && t.status === 'Held');

    setPendingTransactions(pending);
    setTotalPending(pending.reduce((sum, t) => sum + t.amount, 0));

    // Set up interval to refresh data
    const intervalId = setInterval(() => {
      const refreshed = getTransactionHistory().filter(t => t.type === 'Sale' && t.status === 'Held');
      setPendingTransactions(refreshed);
      setTotalPending(refreshed.reduce((sum, t) => sum + t.amount, 0));
    }, 60000); // Refresh every minute

    return () => clearInterval(intervalId);
  }, []);

  if (pendingTransactions.length === 0) {
    return null; // Don't show if no pending earnings
  }

  // Calculate time remaining for a transaction
  const getTimeRemaining = (releaseDate: string): string => {
    const release = new Date(releaseDate);
    const now = new Date();
    const diffMs = release.getTime() - now.getTime();

    if (diffMs <= 0) return 'Processing release...';

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    }

    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} remaining`;
  };

  return (
    <div className="glass p-6 rounded-lg mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Pending Earnings</h2>
        <div className="text-lg font-medium">{totalPending.toFixed(0)} Credits</div>
      </div>

      <div className="text-[#A3A3A3] mb-4">
        Sales earnings are held for 7 days to protect buyers before becoming available for withdrawal.
      </div>

      <div className="space-y-4">
        {pendingTransactions.map(transaction => (
          <div key={transaction.id} className="border border-[#2A2633] rounded-lg p-4">
            <div className="flex justify-between mb-2">
              <div className="font-medium">{transaction.itemName || 'Item Sale'}</div>
              <div className="font-mono">{transaction.amount.toFixed(0)} Credits</div>
            </div>

            <div className="flex justify-between text-sm">
              <div className="text-[#A3A3A3]">
                Sale on {transaction.date}
              </div>
              <div className="text-[#F670C7]">
                Available in {getTimeRemaining(transaction.releaseDate || '')}
              </div>
            </div>

            <div className="mt-3 text-xs text-[#A3A3A3]">
              Release date: {transaction.releaseDate}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingEarningsPanel;
